"use client"

import { useEffect, useRef, type RefObject } from "react"

interface OneShotOptions {
  /** Milliseconds the entrance takes to play once triggered. */
  duration?: number
  /** Fraction of the element that must be visible before the beat starts. */
  threshold?: number
  /** Skip entirely (renders parked at 1 — the "no animation" contract). */
  disabled?: boolean
  /** Called with progress 0..1. Must be referentially stable (useCallback). */
  onProgress: (p: number) => void
}

/* easeOutCubic — the curve the rest of the page's entrances already use
   (cf. PowerNumbers useCountUp). */
const ease = (t: number) => 1 - Math.pow(1 - t, 3)

/**
 * Play an entrance ONCE when the element reaches the viewport, forward only.
 *
 * This is deliberately NOT useScrollScrub. A scrub maps scroll position to
 * progress, so the element keeps drifting for as long as the visitor keeps
 * scrolling and runs backwards when they scroll up — that is a parallax, and
 * EGDE-510 (Raz, 2026-07-29) rejects it for the form section: "no parallax
 * scroll animation with the mouse was defined here. Only the information
 * entrance". A one-shot plays the designed beat and then stays put, so the
 * section never visibly shifts again once it has landed.
 */
export function useOneShotEntrance(
  ref: RefObject<HTMLElement | null>,
  {
    duration = 900,
    threshold = 0.25,
    disabled = false,
    onProgress,
  }: OneShotOptions,
): void {
  /* "Once" has to mean once per MOUNT, not once per effect run. Any change to
     the options re-runs the effect, and a plain local flag would reset with it —
     the entrance would re-park at 0 and play again. PowerNumbers hit exactly
     this under EGDE-529, where a resize across lg replayed the whole beat on a
     block the visitor had already watched settle. A ref survives the re-run.
     (FormShell's duration is breakpoint-dependent, so it is one resize away
     from being that bug.) */
  const playedRef = useRef(false)

  useEffect(() => {
    if (disabled || playedRef.current) {
      onProgress(1)
      return
    }
    const el = ref.current
    if (!el) return

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onProgress(1)
      return
    }

    let raf = 0
    let start = 0

    const frame = (now: number) => {
      if (!start) start = now
      const t = Math.min(1, (now - start) / duration)
      onProgress(ease(t))
      if (t < 1) {
        raf = requestAnimationFrame(frame)
      } else {
        playedRef.current = true
        raf = 0
      }
    }

    // Park at 0 so the first painted frame is the pre-entrance state rather
    // than a jump from the resting position.
    onProgress(0)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || playedRef.current || raf) return
        observer.disconnect()
        start = 0
        raf = requestAnimationFrame(frame)
      },
      { threshold },
    )
    observer.observe(el)

    return () => {
      observer.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [ref, duration, threshold, disabled, onProgress])
}
